import React, { useMemo } from "react";
import {
  Box,
  Heading,
  Tooltip,
  Badge,
  HStack,
  VStack,
  Text,
} from "@chakra-ui/react";

import { TableCell, TableRow } from "@/src/components/ui/table";
import { Button } from "@/src/components/ui/button";

import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { CheckIcon, XIcon } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/src/components/ui/hover-card";
import { OnHolDpayoutsResponse } from "@/src/views/admin/onHoldPayouts";

interface TableBodyContentProps {
  user: OnHolDpayoutsResponse;
  toggleRow: (userId: number) => void;
  expandedRows: Set<number>;
  approveMutation: any;
  rejectMutation: any;
  formatCurrency: (amount: number, currency: string) => string;
  handleApprove: (userId: number) => void;
  handleReject: (userId: number) => void;
}

const TableBodyContent = ({
  user,
  toggleRow,
  expandedRows,
  approveMutation,
  rejectMutation,
  formatCurrency,
  handleApprove,
  handleReject,
}: TableBodyContentProps) => {
  const uniqueMails = useMemo(() => {
    const allMails = user.onHoldPayouts.map((p) => p.mail);
    return Array.from(new Set(allMails));
  }, [user.onHoldPayouts]);

  return (
    <React.Fragment key={user.userId}>
      <TableRow
        className="hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100"
        onClick={() => toggleRow(user.userId)}
      >
        <TableCell>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {expandedRows.has(user.userId) ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        <TableCell>
          <VStack align="start" spacing={1}>
            <Tooltip label={`User ID: ${user.userId}`} placement="top">
              <Button
                variant="link"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/users?userId=${user.userId}`, "_blank");
                }}
                className="font-semibold text-blue-600 hover:text-blue-800 p-0 h-auto"
              >
                User #{user.userId}
              </Button>
            </Tooltip>
            <Tooltip label={`UUID: ${user.uuid}`} placement="top">
              <Text
                fontSize="xs"
                color="gray.500"
                fontFamily="mono"
                cursor="help"
              >
                {user.uuid.substring(0, 8)}...
              </Text>
            </Tooltip>
            <Tooltip
              label={`${uniqueMails.join("\n")}`}
              placement="top"
              className="flex flex-wrap gap-1 whitespace-pre-wrap"
            >
              <Box className="flex flex-col flex-wrap gap-1">
                {uniqueMails.map((mail) => (
                  <Text key={mail} fontSize="xs" color="gray.500">
                    {mail}
                  </Text>
                ))}
              </Box>
            </Tooltip>
          </VStack>
        </TableCell>
        <TableCell>
          <VStack align="start" spacing={1}>
            <Tooltip label={`App ID: ${user.appId}`} placement="top">
              <Text fontSize="sm" fontWeight="medium" cursor="help">
                {user.appId}
              </Text>
            </Tooltip>
            <Tooltip label={`Device ID: ${user.deviceId}`} placement="top">
              <Text
                fontSize="xs"
                color="gray.500"
                fontFamily="mono"
                cursor="help"
              >
                {user.deviceId.substring(0, 12)}...
              </Text>
            </Tooltip>
          </VStack>
        </TableCell>
        <TableCell>
          <Badge colorScheme="green" variant="subtle">
            {user.source}
          </Badge>
        </TableCell>
        <TableCell>
          <HStack spacing={2}>
            <Badge colorScheme="blue" variant="subtle">
              {user.countryCode}
            </Badge>
            <Text fontSize="sm" color="gray.600">
              {user.languageCode.toUpperCase()}
            </Text>
          </HStack>
        </TableCell>
        <TableCell>
          <Text fontSize="lg" fontWeight="bold" color="green.600">
            {formatCurrency(user.sumOfPayouts, user.onHoldPayouts[0].currency)}
          </Text>
        </TableCell>
        <TableCell>
          <HStack spacing={2}>
            <Badge colorScheme="blue" variant="solid" borderRadius="full">
              {user.onHoldPayouts.length}
            </Badge>
            <Text fontSize="sm" color="gray.500">
              pending
            </Text>
          </HStack>
        </TableCell>
        <TableCell>
          <Box position="relative">
            <HoverCard openDelay={0} closeDelay={0}>
              <HoverCardTrigger>
                {user.livenessReferenceImages ? (
                  <Box className="cursor-pointer">
                    <img
                      src={user.livenessReferenceImages?.[0]}
                      alt={`Liveness Reference for User #${user.userId}`}
                      className="w-24 h-24 object-cover rounded-md border border-gray-200"
                    />
                    {user.livenessReferenceImages.length > 1 && (
                      <Badge
                        position="absolute"
                        top="-2"
                        right="2"
                        colorScheme="blue"
                        borderRadius="full"
                        fontSize="xs"
                      >
                        {user.livenessReferenceImages.length}
                      </Badge>
                    )}
                  </Box>
                ) : (
                  <Box className="cursor-pointer">
                    <Text>No liveness reference images</Text>
                  </Box>
                )}
              </HoverCardTrigger>
              {user.livenessReferenceImages && (
                <HoverCardContent className="w-[500px] max-h-[600px] overflow-auto">
                  <VStack spacing={3} align="stretch">
                    <Heading size="sm" color="gray.700">
                      Liveness Reference Images (
                      {user.livenessReferenceImages.length})
                    </Heading>
                    <Box
                      className="flex gap-3 overflow-x-auto pb-2"
                      style={{ scrollbarWidth: "thin" }}
                    >
                      {user.livenessReferenceImages &&
                        user.livenessReferenceImages.map((imageUrl, index) => (
                          <Box
                            key={index}
                            className="relative group flex-shrink-0"
                            borderRadius="md"
                            overflow="hidden"
                            border="1px"
                            borderColor="gray.200"
                            width="200px"
                          >
                            <img
                              src={imageUrl}
                              alt={`Liveness Reference ${index + 1} for User #${
                                user.userId
                              }`}
                              className="w-full h-48 object-cover"
                            />
                            <Badge
                              position="absolute"
                              top={2}
                              left={2}
                              colorScheme="gray"
                              variant="solid"
                              fontSize="xs"
                            >
                              {index + 1}
                            </Badge>
                          </Box>
                        ))}
                    </Box>
                  </VStack>
                </HoverCardContent>
              )}
            </HoverCard>
          </Box>
        </TableCell>
        <TableCell>
          <VStack spacing={2} align="stretch">
            <Button
              variant="default"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleApprove(user.userId);
              }}
              disabled={approveMutation.isPending}
              className="text-xs bg-green-600 hover:bg-green-700 text-white gap-1 w-32"
            >
              <CheckIcon className="h-4 w-4" />
              {approveMutation.isPending ? "Approving..." : "Approve"}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleReject(user.userId);
              }}
              disabled={rejectMutation.isPending}
              className="text-xs bg-red-600 hover:bg-red-700 text-white gap-1 w-32"
            >
              <XIcon className="h-4 w-4" />
              {rejectMutation.isPending ? "Rejecting..." : "Reject"}
            </Button>
          </VStack>
        </TableCell>
      </TableRow>

      {expandedRows.has(user.userId) && (
        <TableRow className="bg-blue-50">
          <TableCell colSpan={7} className="p-0">
            <Box p={6} bgGradient="linear(to-r, blue.50, indigo.50)">
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Heading size="sm" color="gray.700">
                    Payout Details
                  </Heading>
                  <Badge colorScheme="blue" variant="outline">
                    {user.onHoldPayouts.length} item
                    {user.onHoldPayouts.length !== 1 ? "s" : ""}
                  </Badge>
                </HStack>

                <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {user.onHoldPayouts.map(
                    (
                      payout: OnHolDpayoutsResponse["onHoldPayouts"][number],
                    ) => (
                      <Box
                        key={payout.payoutId}
                        p={4}
                        bg="white"
                        borderRadius="lg"
                        boxShadow="sm"
                        border="1px"
                        borderColor="gray.200"
                        className="hover:shadow-md transition-shadow"
                      >
                        <VStack align="stretch" spacing={3}>
                          <HStack justify="space-between">
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="gray.700"
                            >
                              Payout #{payout.payoutId}
                            </Text>
                            <Badge
                              colorScheme={"yellow"}
                              borderRadius="full"
                              px={2}
                            >
                              {payout.status.replace("_", " ").toUpperCase()}
                            </Badge>
                          </HStack>

                          <VStack align="stretch" spacing={2}>
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Amount:
                              </Text>
                              <Text fontSize="sm" fontWeight="semibold">
                                {formatCurrency(
                                  payout.valueEur,
                                  payout.currency,
                                )}
                              </Text>
                            </HStack>

                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Coins:
                              </Text>
                              <HStack spacing={1}>
                                <Text
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="orange.500"
                                >
                                  {payout.coins.toLocaleString()}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  coins
                                </Text>
                              </HStack>
                            </HStack>

                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Type:
                              </Text>
                              <Badge
                                colorScheme="purple"
                                variant="subtle"
                                textTransform="capitalize"
                              >
                                {payout.optionType}
                              </Badge>
                            </HStack>

                            <HStack justify="space-between" align="start">
                              <Text fontSize="sm" color="gray.600">
                                Email:
                              </Text>
                              <Tooltip label={payout.mail} placement="top">
                                <Text
                                  fontSize="sm"
                                  fontFamily="mono"
                                  textAlign="right"
                                  wordBreak="break-all"
                                  cursor="help"
                                  maxWidth="120px"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                >
                                  {payout.mail}
                                </Text>
                              </Tooltip>
                            </HStack>

                            {payout.fraudmlInferenceResult !== null && (
                              <VStack
                                align="stretch"
                                spacing={2}
                                mt={3}
                                p={3}
                                bg="gray.50"
                                borderRadius="md"
                              >
                                <Text
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="gray.700"
                                >
                                  Fraud Analysis
                                </Text>
                                <HStack justify="space-between">
                                  <Text fontSize="sm" color="gray.600">
                                    Status:
                                  </Text>
                                  <Badge
                                    colorScheme={
                                      payout.fraudmlInferenceResult.isFraud
                                        ? "red"
                                        : "green"
                                    }
                                    variant="solid"
                                  >
                                    {payout.fraudmlInferenceResult.isFraud
                                      ? "Fraud"
                                      : "Legit"}
                                  </Badge>
                                </HStack>
                                <HStack justify="space-between">
                                  <Text fontSize="sm" color="gray.600">
                                    Legit Probability:
                                  </Text>
                                  <Tooltip
                                    label={
                                      payout.fraudmlInferenceResult
                                        .probabilityLegit
                                    }
                                    placement="top"
                                  >
                                    <Text
                                      fontSize="sm"
                                      fontWeight="semibold"
                                      color="green.600"
                                      cursor="pointer"
                                    >
                                      {payout.fraudmlInferenceResult.probabilityLegit.toFixed(
                                        2,
                                      )}
                                    </Text>
                                  </Tooltip>
                                </HStack>
                                <HStack justify="space-between">
                                  <Text fontSize="sm" color="gray.600">
                                    Fraud Probability:
                                  </Text>
                                  <Tooltip
                                    label={
                                      payout.fraudmlInferenceResult
                                        .probabilityFraud
                                    }
                                    placement="top"
                                  >
                                    <Text
                                      fontSize="sm"
                                      fontWeight="semibold"
                                      color="red.600"
                                      cursor="pointer"
                                    >
                                      {payout.fraudmlInferenceResult.probabilityFraud.toFixed(
                                        2,
                                      )}
                                    </Text>
                                  </Tooltip>
                                </HStack>
                              </VStack>
                            )}
                          </VStack>
                        </VStack>
                      </Box>
                    ),
                  )}
                </Box>
              </VStack>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

export default TableBodyContent;
